import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { WymhSiteConfig } from "@/mocks/wymh-config";

export const WYMH_NAV = [
  { id: "wymh-library", label: "漫画库" },
  { id: "wymh-preview", label: "软件预览" },
  { id: "wymh-reading", label: "阅读体验" },
  { id: "wymh-faq", label: "读者服务" },
];

interface WymhSiteHeaderProps {
  config: WymhSiteConfig;
}

export function wymhJumpToSection(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.pageYOffset;
  // 直接定位，不做平滑滚动；模块顶部与屏幕顶部对齐
  window.scrollTo(0, top);
}

export default function WymhSiteHeader({ config }: WymhSiteHeaderProps) {
  const [solid, setSolid] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setSolid(window.pageYOffset > 40);
    window.addEventListener("scroll", onScroll, false);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll, false);
  }, []);

  const handleJump = (id: string) => {
    wymhJumpToSection(id);
    setMenuOpen(false);
  };

  return (
    <header className={`wymh-header fixed top-0 left-0 right-0 z-50 w-full ${solid ? "is-solid" : ""}`}>
      <div className="w-full px-4 md:px-8 h-[64px] md:h-[78px] flex items-center justify-between">
        <div className="flex items-center gap-6 md:gap-10">
          <a
            href={config.domainUrl}
            className="flex items-center gap-2 md:gap-3 cursor-pointer"
            aria-label={`${config.brand} 官方网站 ${config.domain}`}
          >
            <span className="w-10 h-10 md:w-11 md:h-11 rounded-xl overflow-hidden flex items-center justify-center shrink-0 bg-foreground-950">
              <img
                src="/images/wymh-app-icon.png"
                alt="五一漫画App图标"
                title="五一漫画官方图标"
                className="w-full h-full object-top"
              />
            </span>
            <span
              className={`font-label text-base md:text-lg font-bold tracking-tight whitespace-nowrap ${
                solid ? "text-foreground-950" : "text-background-50"
              }`}
            >
              {config.domain}
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-7">
            {WYMH_NAV.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleJump(item.id)}
                className={`font-label text-sm font-medium cursor-pointer whitespace-nowrap ${
                  solid ? "text-foreground-700 hover:text-primary-600" : "text-background-100 hover:text-primary-300"
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/download"
            className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-bold cursor-pointer whitespace-nowrap"
          >
            <i className="ri-download-2-line text-base w-4 h-4 flex items-center justify-center"></i>
            下载App
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="打开菜单"
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer ${
              solid ? "text-foreground-950 bg-background-100" : "text-background-50 bg-foreground-950/40"
            }`}
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-4 flex flex-col gap-1">
            {WYMH_NAV.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleJump(item.id)}
                className="text-left px-3 py-3 rounded-lg text-foreground-800 font-label text-sm font-medium hover:bg-primary-50 cursor-pointer"
              >
                {item.label}
              </button>
            ))}
            <Link
              to="/download"
              onClick={() => setMenuOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary-500 text-background-50 font-label text-sm font-bold cursor-pointer"
            >
              <i className="ri-download-2-line w-4 h-4 flex items-center justify-center"></i>
              下载App
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}